import { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import { handleInputChangeWithScroll } from "../../../utils/editIframe";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import {
  executeValidation,
  handleInputChangeWithValidation,
  handleMediaSelectWithValidation,
  handleResetWithValidation,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Top", path: `/` },
];

function TopEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const contentKey = "topContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({
    // スライド
    slide_a_1: {},
    slide_a_2: {},
    slide_a_3: {},
    slide_b_1: {},
    slide_b_2: {},
    slide_b_3: {},
    slide_c_1: {},
    slide_c_2: {},
    slide_c_3: {},
    slide_d_1: {},
    slide_d_2: {},
    slide_d_3: {},
    slide_e_1: {},
    slide_e_2: {},
    slide_e_3: {},
    slide_f_1: {},
    slide_f_2: {},
    slide_f_3: {},
    slide_g_1: {},
    slide_g_2: {},
    slide_g_3: {},
    slide_h_1: {},
    slide_h_2: {},
    slide_h_3: {},
    slide_i_1: {},
    slide_i_2: {},
    slide_i_3: {},
    // メニュー
    menu_a_link: "",
    menu_a_title: "",
    menu_a_content: "",
    menu_a_thumbnail: {},
    menu_b_link: "",
    menu_b_title: "",
    menu_b_content: "",
    menu_b_thumbnail: {},
    // 新規訪問者
    newvisitor_text: "",
    newvisitor_image: {},
    // 共通データ
    postal_code: "",
    address: "",
    phone_number: "",
  });

  // データ取得処理
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_top_content/`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchMediaFiles();
  }, [fetchPageContentData, fetchMediaFiles]);

  // バリデーションルールを定義
  const validationRules = {
    menu_a_title: { required: true },
    menu_a_link: { required: true },
    menu_a_content: { required: true },
    menu_b_title: { required: true },
    menu_b_link: { required: true },
    menu_b_content: { required: true },
    newvisitor_text: { required: true },
    postal_code: { required: true },
    address: { required: true },
    phone_number: { required: true },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    menu_a_title: "メニューAタイトル",
    menu_a_link: "メニューAリンク",
    menu_a_content: "メニューA内容",
    menu_b_title: "メニューBタイトル",
    menu_b_link: "メニューBリンク",
    menu_b_content: "メニューB内容",
    newvisitor_text: "新規訪問者テキスト",
    postal_code: "郵便番号",
    address: "住所",
    phone_number: "電話番号",
  };

  // 更新処理
  const handlePageDataSubmit = async () => {
    const formData = { page_content: pageContent };

    // バリデーション実行
    if (
      !executeValidation(pageContent, validationRules, fieldLabels, setMessage)
    ) {
      return;
    }

    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_page_top_content/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setMessage(data.message);
      await fetchPageContentData(); // 最新データ再取得

      // 成功時にページトップまでスクロール
      setTimeout(() => {
        const editArea = document.querySelector(".editarea");
        if (editArea) {
          editArea.scrollTop = 0;
        }
      }, 100);

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      setMessage("更新に失敗しました");
      setTimeout(() => setMessage(""), 3000);
    }
    setLoadingText("");
  };

  // リセット処理
  const handleReset = () => {
    handleResetWithValidation(fetchPageContentData);
  };

  // 入力変更処理
  const handleInputChange = (e) => {
    handleInputChangeWithValidation(
      e,
      setPageContent,
      handleInputChangeWithScroll,
      {
        contentKey,
        currentData: pageContent,
        setData: setPageContent,
      }
    );
  };

  // メディア選択処理
  const handleMediaSelect = (fieldName, media) => {
    handleMediaSelectWithValidation(fieldName, media, setPageContent);
  };

  // ファイルドロップ処理
  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setLoadingText("アップロード中");

      // ドラッグ&ドロップまたはファイル選択の両方に対応
      const files = e.dataTransfer?.files || e.target?.files;
      const file = files?.[0];

      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        await fetchMediaFiles();
        if (data.images?.[0]) {
          selectMedia(data.images[0]);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
        alert("アップロードに失敗しました");
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia]
  );

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />

            <div className="form">
              {/* ヒーローヘッダー */}
              <section>
                {/* スライドA */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドA1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_a_1"
                    selectedMedia={pageContent.slide_a_1}
                    onSelect={(media) => handleMediaSelect("slide_a_1", media)}
                  />
                  <MediaSelector
                    label="スライドA2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_a_2"
                    selectedMedia={pageContent.slide_a_2}
                    onSelect={(media) => handleMediaSelect("slide_a_2", media)}
                  />
                  <MediaSelector
                    label="スライドA3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_a_3"
                    selectedMedia={pageContent.slide_a_3}
                    onSelect={(media) => handleMediaSelect("slide_a_3", media)}
                  />
                </div>

                {/* スライドB */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドB1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_b_1"
                    selectedMedia={pageContent.slide_b_1}
                    onSelect={(media) => handleMediaSelect("slide_b_1", media)}
                  />
                  <MediaSelector
                    label="スライドB2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_b_2"
                    selectedMedia={pageContent.slide_b_2}
                    onSelect={(media) => handleMediaSelect("slide_b_2", media)}
                  />
                  <MediaSelector
                    label="スライドB3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_b_3"
                    selectedMedia={pageContent.slide_b_3}
                    onSelect={(media) => handleMediaSelect("slide_b_3", media)}
                  />
                </div>

                {/* スライドC */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドC1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_c_1"
                    selectedMedia={pageContent.slide_c_1}
                    onSelect={(media) => handleMediaSelect("slide_c_1", media)}
                  />
                  <MediaSelector
                    label="スライドC2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_c_2"
                    selectedMedia={pageContent.slide_c_2}
                    onSelect={(media) => handleMediaSelect("slide_c_2", media)}
                  />
                  <MediaSelector
                    label="スライドC3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_c_3"
                    selectedMedia={pageContent.slide_c_3}
                    onSelect={(media) => handleMediaSelect("slide_c_3", media)}
                  />
                </div>

                {/* スライドD */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドD1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_d_1"
                    selectedMedia={pageContent.slide_d_1}
                    onSelect={(media) => handleMediaSelect("slide_d_1", media)}
                  />
                  <MediaSelector
                    label="スライドD2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_d_2"
                    selectedMedia={pageContent.slide_d_2}
                    onSelect={(media) => handleMediaSelect("slide_d_2", media)}
                  />
                  <MediaSelector
                    label="スライドD3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_d_3"
                    selectedMedia={pageContent.slide_d_3}
                    onSelect={(media) => handleMediaSelect("slide_d_3", media)}
                  />
                </div>

                {/* スライドE */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドE1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_e_1"
                    selectedMedia={pageContent.slide_e_1}
                    onSelect={(media) => handleMediaSelect("slide_e_1", media)}
                  />
                  <MediaSelector
                    label="スライドE2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_e_2"
                    selectedMedia={pageContent.slide_e_2}
                    onSelect={(media) => handleMediaSelect("slide_e_2", media)}
                  />
                  <MediaSelector
                    label="スライドE3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_e_3"
                    selectedMedia={pageContent.slide_e_3}
                    onSelect={(media) => handleMediaSelect("slide_e_3", media)}
                  />
                </div>

                {/* スライドF */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドF1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_f_1"
                    selectedMedia={pageContent.slide_f_1}
                    onSelect={(media) => handleMediaSelect("slide_f_1", media)}
                  />
                  <MediaSelector
                    label="スライドF2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_f_2"
                    selectedMedia={pageContent.slide_f_2}
                    onSelect={(media) => handleMediaSelect("slide_f_2", media)}
                  />
                  <MediaSelector
                    label="スライドF3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_f_3"
                    selectedMedia={pageContent.slide_f_3}
                    onSelect={(media) => handleMediaSelect("slide_f_3", media)}
                  />
                </div>

                {/* スライドG */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドG1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_g_1"
                    selectedMedia={pageContent.slide_g_1}
                    onSelect={(media) => handleMediaSelect("slide_g_1", media)}
                  />
                  <MediaSelector
                    label="スライドG2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_g_2"
                    selectedMedia={pageContent.slide_g_2}
                    onSelect={(media) => handleMediaSelect("slide_g_2", media)}
                  />
                  <MediaSelector
                    label="スライドG3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_g_3"
                    selectedMedia={pageContent.slide_g_3}
                    onSelect={(media) => handleMediaSelect("slide_g_3", media)}
                  />
                </div>

                {/* スライドH */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドH1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_h_1"
                    selectedMedia={pageContent.slide_h_1}
                    onSelect={(media) => handleMediaSelect("slide_h_1", media)}
                  />
                  <MediaSelector
                    label="スライドH2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_h_2"
                    selectedMedia={pageContent.slide_h_2}
                    onSelect={(media) => handleMediaSelect("slide_h_2", media)}
                  />
                  <MediaSelector
                    label="スライドH3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_h_3"
                    selectedMedia={pageContent.slide_h_3}
                    onSelect={(media) => handleMediaSelect("slide_h_3", media)}
                  />
                </div>

                {/* スライドI */}
                <div className="img-form">
                  <MediaSelector
                    label="スライドI1"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_i_1"
                    selectedMedia={pageContent.slide_i_1}
                    onSelect={(media) => handleMediaSelect("slide_i_1", media)}
                  />
                  <MediaSelector
                    label="スライドI2"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_i_2"
                    selectedMedia={pageContent.slide_i_2}
                    onSelect={(media) => handleMediaSelect("slide_i_2", media)}
                  />
                  <MediaSelector
                    label="スライドI3"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="slide_i_3"
                    selectedMedia={pageContent.slide_i_3}
                    onSelect={(media) => handleMediaSelect("slide_i_3", media)}
                  />
                </div>
              </section>

              {/* メニューA */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <input
                        type="text"
                        name="menu_a_title"
                        value={pageContent.menu_a_title}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="メニューAタイトル"
                        data-required="true"
                      />
                      <textarea
                        className="input2"
                        name="menu_a_content"
                        value={pageContent.menu_a_content}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="メニューA説明"
                        data-required="true"
                      />
                      <input
                        type="text"
                        name="menu_a_link"
                        value={pageContent.menu_a_link}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="メニューAリンク"
                        data-required="true"
                      />
                      <div className="input3 img-form">
                        <MediaSelector
                          label="メニューAサムネイル"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="menu_a_thumbnail"
                          selectedMedia={pageContent.menu_a_thumbnail}
                          onSelect={(media) =>
                            handleMediaSelect("menu_a_thumbnail", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* メニューB */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <input
                        type="text"
                        name="menu_b_title"
                        value={pageContent.menu_b_title}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="メニューBタイトル"
                        data-required="true"
                      />
                      <textarea
                        className="input2"
                        name="menu_b_content"
                        value={pageContent.menu_b_content}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="メニューB説明"
                        data-required="true"
                      />
                      <input
                        type="text"
                        name="menu_b_link"
                        value={pageContent.menu_b_link}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="メニューBリンク"
                        data-required="true"
                      />
                      <div className="input3 img-form">
                        <MediaSelector
                          label="メニューBサムネイル"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="menu_b_thumbnail"
                          selectedMedia={pageContent.menu_b_thumbnail}
                          onSelect={(media) =>
                            handleMediaSelect("menu_b_thumbnail", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 新規訪問者 */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <textarea
                        className="input2"
                        name="newvisitor_text"
                        value={pageContent.newvisitor_text}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="新規訪問者向けテキスト"
                        data-required="true"
                      />
                      <div className="input3 img-form">
                        <MediaSelector
                          label="新規訪問者向け画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="newvisitor_image"
                          selectedMedia={pageContent.newvisitor_image}
                          onSelect={(media) =>
                            handleMediaSelect("newvisitor_image", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 共通データ */}
              <section>
                <div className="area1">
                  <input
                    type="text"
                    name="postal_code"
                    value={pageContent.postal_code}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="郵便番号"
                    data-required="true"
                  />
                  <input
                    type="text"
                    name="address"
                    value={pageContent.address}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="住所"
                    data-required="true"
                  />
                  <input
                    type="text"
                    name="phone_number"
                    value={pageContent.phone_number}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="電話番号"
                    data-required="true"
                  />
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>

            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe path="/" data={pageContent} contentKey={contentKey} />
    </>
  );
}

export default function TopEditPage() {
  return (
    <MediaProvider>
      <TopEditPageContent />
    </MediaProvider>
  );
}
